import React from "react";
import { Sparkles, Check, X, ChevronRight, Info, ArrowDownUp } from "lucide-react";
import { MUSCLE_LABELS } from "./BodyDiagram";

/**
 * Treatment Map — AI-generated priority-graded muscle plan.
 *
 * Renders ABOVE the body diagram on the Diagram phase. Three priority buckets:
 *   - Primary   (terracotta) — top 2-4 focus areas
 *   - Secondary (orange/amber) — 2-5 supporting areas
 *   - Related   (yellow)     — compensatory / referral / check
 *
 * Therapists can:
 *   - Generate / regenerate the map
 *   - Accept (merges into worked_areas)
 *   - Drop a single muscle (X chip)
 *   - Expand to read the AI explanation
 */
export default function TreatmentMapPanel({
  map,
  workedAreas,
  onGenerate,
  onAccept,
  onMuscleClick,
  onRemoveMuscle,
  generating,
}) {
  const [showExplanation, setShowExplanation] = React.useState(true);
  const hasMap = Boolean(map?.primary?.length || map?.secondary?.length || map?.related?.length);
  const total = (map?.primary?.length || 0) + (map?.secondary?.length || 0) + (map?.related?.length || 0);
  const allInWorked = hasMap && [...(map.primary || []), ...(map.secondary || []), ...(map.related || [])].every((m) => workedAreas.includes(m));

  // Iter AI — provenance badge state
  const isAiGenerated = !!map?.source && map.source === "ai";
  const wasEdited = !!map?.edited_at;
  const badgeTime = map?.generated_at ? new Date(map.generated_at) : null;
  const badgeTimeStr = badgeTime && !isNaN(badgeTime.getTime())
    ? badgeTime.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : null;
  const badgeLabel = wasEdited
    ? "AI-generated · edited"
    : badgeTimeStr ? `Auto-generated ${badgeTimeStr}` : "Auto-generated";

  return (
    <div
      data-testid="treatment-map-panel"
      className="rounded-3xl border border-sage-500/30 bg-gradient-to-br from-paper-50 to-white p-6 sm:p-7 mb-6"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="label-overline text-sage-700 mb-1.5 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Treatment map
          </div>
          <h2 className="font-heading text-2xl text-ink-900">AI-prioritized treatment plan</h2>
          {hasMap && isAiGenerated && (
            <div className="mt-2 inline-flex items-center gap-2" data-testid="treatment-map-provenance-badge">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${
                  wasEdited
                    ? "bg-amber-100 text-amber-800"
                    : "bg-sage-100 text-sage-700"
                }`}
                title={wasEdited ? "You modified the AI plan" : "Plan generated by AI"}
              >
                <Sparkles className="w-3 h-3" />
                {badgeLabel}
              </span>
              <button
                type="button"
                onClick={onGenerate}
                disabled={generating}
                data-testid="treatment-map-badge-regenerate"
                className="text-[11px] text-ink-600 hover:text-ink-900 underline-offset-2 hover:underline disabled:opacity-50"
              >
                Regenerate
              </button>
            </div>
          )}
          <p className="text-sm text-ink-700 mt-1 max-w-2xl">
            Based on today&rsquo;s intake, posture findings, and continuity-of-care context.
            Accept to highlight on the diagram, then refine by tapping muscles individually.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasMap && !allInWorked && (
            <button
              onClick={onAccept}
              data-testid="treatment-map-accept"
              className="min-h-[40px] px-4 rounded-full bg-sage-600 text-white text-sm font-medium hover:bg-sage-700 inline-flex items-center gap-1.5"
            >
              <Check className="w-3.5 h-3.5" /> Accept all
            </button>
          )}
          {hasMap && allInWorked && (
            <span className="min-h-[40px] px-4 rounded-full bg-sage-500/15 text-sage-700 text-xs font-medium inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Accepted
            </span>
          )}
          <button
            onClick={onGenerate}
            disabled={generating}
            data-testid="treatment-map-generate"
            className="min-h-[40px] px-4 rounded-full bg-ink-900 text-white text-sm font-medium hover:bg-ink-800 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {generating ? "Generating…" : hasMap ? "Regenerate" : "Generate"}
          </button>
        </div>
      </div>

      {!hasMap && !generating && (
        <div className="text-center py-8 text-sm text-ink-500" data-testid="treatment-map-empty">
          Tap <strong className="text-ink-900">Generate</strong> to build a treatment map from this client&rsquo;s intake + history.
        </div>
      )}

      {generating && (
        <div className="text-center py-8 text-sm text-ink-500 animate-pulse" data-testid="treatment-map-loading">
          Drafting clinical treatment map…
        </div>
      )}

      {hasMap && (
        <div className="space-y-4">
          <PriorityRow
            label="Primary focus"
            muscles={map.primary || []}
            color="terracotta"
            workedAreas={workedAreas}
            onMuscleClick={onMuscleClick}
            onRemoveMuscle={onRemoveMuscle}
            testIdPrefix="tmap-primary"
          />
          <PriorityRow
            label="Secondary focus"
            muscles={map.secondary || []}
            color="amber"
            workedAreas={workedAreas}
            onMuscleClick={onMuscleClick}
            onRemoveMuscle={onRemoveMuscle}
            testIdPrefix="tmap-secondary"
          />
          <PriorityRow
            label="Related / compensatory"
            muscles={map.related || []}
            color="yellow"
            workedAreas={workedAreas}
            onMuscleClick={onMuscleClick}
            onRemoveMuscle={onRemoveMuscle}
            testIdPrefix="tmap-related"
          />

          {/* Treatment workflow — practical hands-on order, separate from clinical priority */}
          {Array.isArray(map.workflow) && map.workflow.length > 0 && (
            <div className="pt-4 mt-2 border-t border-paper-200" data-testid="treatment-map-workflow">
              <div className="label-overline mb-1.5 inline-flex items-center gap-1.5">
                <ArrowDownUp className="w-3 h-3" /> Treatment workflow
              </div>
              <p className="text-xs text-ink-500 mb-3">
                Practical hands-on order, grouped by client position. Colors still show clinical priority.
              </p>
              <ol className="space-y-2.5">
                {map.workflow.map((phase, i) => (
                  <li key={`${phase.position}-${i}`} className="flex gap-3" data-testid={`tmap-workflow-phase-${i}`}>
                    <span className="w-6 h-6 rounded-full bg-ink-900 text-white text-[11px] font-semibold flex items-center justify-center shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-ink-900 capitalize">{phase.position}</span>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {(phase.muscles || []).map((m) => {
                          const bucket = (map.primary || []).includes(m)
                            ? "bg-terracotta-500/15 text-terracotta-600 border-terracotta-500/40"
                            : (map.secondary || []).includes(m)
                            ? "bg-amber-500/15 text-amber-700 border-amber-500/40"
                            : "bg-yellow-500/15 text-yellow-700 border-yellow-500/40";
                          return (
                            <button
                              key={m}
                              type="button"
                              onClick={() => onMuscleClick?.(m)}
                              data-testid={`tmap-workflow-${i}-${m}`}
                              className={`px-2.5 py-0.5 rounded-full border text-xs hover:underline ${bucket}`}
                            >
                              {MUSCLE_LABELS[m] || m}
                            </button>
                          );
                        })}
                      </div>
                      {phase.note && <p className="text-xs text-ink-500 mt-1">{phase.note}</p>}
                      {(phase.muscles || []).some((m) => map.guidance?.[m]) && (
                        <ul className="mt-1.5 space-y-1" data-testid={`tmap-workflow-guidance-${i}`}>
                          {(phase.muscles || []).map((m) => {
                            const g = map.guidance?.[m];
                            if (!g || (!g.intervention_label && !g.position_uncommon)) return null;
                            return (
                              <li key={m} className="text-xs text-ink-600 pl-2 border-l-2 border-sage-500/40" data-testid={`tmap-guidance-${m}`}>
                                <span className="font-medium text-ink-800">{MUSCLE_LABELS[m] || m}</span>
                                {g.intervention_label && (
                                  <span className="block text-ink-600" data-testid={`tmap-intervention-${m}`}>{g.intervention_label}</span>
                                )}
                                {g.position_uncommon && (
                                  <span className="block text-ink-500" data-testid={`tmap-position-why-${m}`}>
                                    Why {phase.position}: {g.position_why}
                                    {g.position_alternatives_label && <> · Alternative: {g.position_alternatives_label}</>}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Why these muscles */}
          {map.explanation && (
            <div className="pt-4 mt-2 border-t border-paper-200">
              <button
                onClick={() => setShowExplanation((v) => !v)}
                data-testid="treatment-map-explain-toggle"
                className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900"
              >
                <Info className="w-4 h-4 text-sage-600" />
                <span className="font-medium">Why these muscles?</span>
                <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showExplanation ? "rotate-90" : ""}`} />
              </button>
              {showExplanation && (
                <p
                  data-testid="treatment-map-explanation"
                  className="text-sm text-ink-800 leading-relaxed mt-2 pl-6 italic"
                >
                  {map.explanation}
                </p>
              )}
            </div>
          )}

          {/* Compact legend */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 mt-3 border-t border-paper-200 text-xs text-ink-500" data-testid="treatment-map-legend">
            <LegendDot color="terracotta" label="Primary" />
            <LegendDot color="amber" label="Secondary" />
            <LegendDot color="yellow" label="Related" />
            <span className="text-[11px]">{total} muscles suggested</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PriorityRow({ label, muscles, color, workedAreas, onMuscleClick, onRemoveMuscle, testIdPrefix }) {
  if (!muscles?.length) return null;
  const chipCls =
    color === "terracotta"
      ? "bg-terracotta-500/15 text-terracotta-600 border-terracotta-500/40 hover:bg-terracotta-500/25"
      : color === "amber"
      ? "bg-amber-500/15 text-amber-700 border-amber-500/40 hover:bg-amber-500/25"
      : "bg-yellow-500/15 text-yellow-700 border-yellow-500/40 hover:bg-yellow-500/25";
  return (
    <div data-testid={testIdPrefix}>
      <div className="label-overline mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {muscles.map((m) => {
          const inWorked = workedAreas.includes(m);
          return (
            <span
              key={m}
              data-testid={`${testIdPrefix}-${m}`}
              className={`inline-flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full border text-xs transition-all ${chipCls} ${inWorked ? "ring-1 ring-current" : "opacity-90"}`}
            >
              <button
                type="button"
                onClick={() => onMuscleClick?.(m)}
                className="hover:underline"
                aria-label={`Info about ${MUSCLE_LABELS[m] || m}`}
              >
                {MUSCLE_LABELS[m] || m}
              </button>
              <button
                type="button"
                onClick={() => onRemoveMuscle?.(m)}
                className="p-1 rounded-full hover:bg-white/40"
                aria-label={`Remove ${MUSCLE_LABELS[m] || m} from map`}
                data-testid={`${testIdPrefix}-${m}-remove`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  const dot =
    color === "terracotta" ? "bg-terracotta-500" : color === "amber" ? "bg-amber-500" : "bg-yellow-500";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
      <span>{label}</span>
    </span>
  );
}
